// Klien untuk sisi tamu — TANPA token.
//
// Sengaja TIDAK memakai `api` dari lib/api.ts. Klien itu menempelkan
// Authorization dari sesi admin yang kebetulan tersimpan di peramban yang
// sama. Kalau kapten membuka remote di HP-nya sendiri, permintaan tamu akan
// membawa token admin — dan seluruh alasan grup /t ada jadi tidak berlaku
// pada perangkat yang paling sering dipakai mencobanya.

import axios from 'axios'

// baseURL '/api/t' — bukan '/t'.
//
// nginx di depan karaoke hanya meneruskan /api dan /auth ke backend. Alamat
// /t akan dijawab index.html oleh container SPA, dan axios gagal dengan
// "Unexpected token <" — pesan yang tidak menyebut nginx sama sekali dan
// mengarahkan orang mencari kesalahan di kode klien.
const t = axios.create({ baseURL: '/api/t' })

export type BarisAntrean = {
  id: string
  urutan: number
  status: 'menunggu' | 'diputar'
  geser_nada: number
  tempo_persen: number
  vokal_panduan: boolean
  diminta_oleh: string | null
  song_id: string | null
  judul: string
  artis: string
  nada_dasar: string | null
  durasi_detik: number | null
  ada_lirik: boolean
  ada_instrumental: boolean
  ada_melodi: boolean
  // 3 Okt 2026 — katalog luar + kontrol audio (migrate_katalog_luar.sql).
  // song_id NULL untuk item spotify/youtube.
  sumber?: SumberAntre
  ref?: string | null
  sampul?: string
  ada_vokal?: boolean
  musik_nyala?: boolean
  jeda?: boolean
  volume?: number
  /** 8 Okt 2026: ulang lagu ini terus (loop) sampai dimatikan dari remote. */
  ulang?: boolean
  pemutar?: LaporPemutar | null
  /** 21 Okt 2026: geser lirik LRCLIB per video (detik, + = lirik lebih lambat), dari remote. */
  lirik_geser?: number | null
  /** Durasi rekaman LRCLIB yang dipakai liriknya (detik) — beda jauh = versi lain. */
  lirik_durasi?: number | null
  /** 27 Okt 2026: lirik kita untuk video ini dinyalakan staf (bawaan: mati). */
  lirik_tampil?: boolean
}

export type SumberAntre = 'lokal' | 'spotify' | 'youtube'

/** Laporan pemutar TV: mode audio yang benar-benar dipakai. */
export type LaporPemutar = {
  mode: 'stem' | 'tukar' | 'asli' | 'youtube' | 'spotify' | 'kosong'
  vokal_bisa: boolean; musik_bisa: boolean; nada_bisa: boolean
  tempo_bisa: boolean; jeda_bisa: boolean; volume_bisa: boolean
  pesan: string
}

export type KeadaanRoom = {
  // Sidik keadaan. Dikirim balik lewat ?sejak= pada permintaan berikutnya;
  // kalau server melihat sidik yang sama, ia menjawab {tetap:true} tanpa
  // menjalankan kueri besarnya. Lihat TamuRoom di backend/handlers/tamu.go.
  sidik?: string
  room: {
    kode: string; nama: string; outlet: string
    status: string; tamu: string | null; sisa_detik: number | null
  } | null
  antrean: BarisAntrean[]
  papan: { nama: string; lagu: number; terbaik: number; rerata: number; mode_campur: boolean }[]
  posisi?: PosisiPutar | null
}

/** 5 Okt 2026: posisi lagu yang dilaporkan pemutar TV (backend/handlers/
 *  posisi.go). Disimpan di memori server, DI LUAR sidik — jadi ikut juga di
 *  jawaban "tetap". `umur_ms` = umur laporan menurut jam server saat menjawab;
 *  remote menginterpolasi dari situ, bukan dari jam HP. */
export type PosisiPutar = {
  id: string; posisi: number; durasi: number; laju: number; jalan: boolean; umur_ms: number
}

// Jawaban ringkas saat tidak ada yang berubah. ~60 byte, bukan beberapa KB.
export type RoomTetap = { tetap: true; sidik: string; sisa_detik: number | null; posisi?: PosisiPutar | null }

export const tidakBerubah = (j: KeadaanRoom | RoomTetap): j is RoomTetap =>
  (j as RoomTetap).tetap === true

export type LaguSiap = {
  id: string; judul: string; artis: string; bahasa: string
  genre: string | null; tahun: number | null
  nada_dasar: string | null; durasi_detik: number | null
  ada_lirik: boolean; ada_melodi: boolean
}

export type BerkasLagu = {
  judul: string; artis: string; nada_dasar: string | null
  lirik: { t: number; teks: string }[]
  tersinkron: boolean
  melodi: { t: number; d: number; m: number }[] | null
  melodi_offset: { semitone: number; detik: number }
  mode_skor: 'nada' | 'irama'
  // 26 Okt: kanal_vokal = video karaoke vendor lama (vokal di satu kanal stereo).
  trek: { jenis: string; mutu: string; path: string; durasi_detik: number | null; kanal_vokal?: 'L' | 'R' | null; path_cadangan?: string | null }[]
}

export const pesanGalat = (e: unknown): string => {
  const ax = e as { response?: { data?: { error?: string } }; message?: string }
  return ax?.response?.data?.error || ax?.message || 'Gagal menghubungi server'
}

// `sejak` = sidik yang sudah dipegang layar. Kosong = minta jawaban penuh.
// `signal` = pembatal; dipakai supaya permintaan yang sudah tidak relevan
// (layar ditutup, atau permintaan lama masih menggantung) benar-benar
// dihentikan, bukan cuma diabaikan hasilnya.
export const ambilRoom = (kode: string, sejak = '', signal?: AbortSignal) =>
  t.get<KeadaanRoom | RoomTetap>(`/${kode}`, {
    params: sejak ? { sejak } : undefined,
    signal,
  }).then(r => r.data)

export const cariLagu = (kode: string, q: string, bahasa = '') =>
  t.get<LaguSiap[]>(`/${kode}/lagu`, { params: { q, bahasa } }).then(r => r.data)

// 29 Sep 2026 — top chart & promo untuk remote tablet.
export type LaguTop = {
  posisi: number; judul: string; artis: string
  song_id: string | null; ref_eksternal: string | null
  bisa: boolean; nada_dasar: string | null; durasi_detik: number | null
  jml_antre?: number; ada_lirik: boolean; ada_melodi: boolean
}
export type PromoTamu = {
  jenis: 'promo' | 'event'; judul: string; deskripsi: string
  gambar: string; tanggal: string; outlet: string; tautan: string
}
export const ambilTop = (kode: string, sumber: 'spotify' | 'youtube' | 'kami', bahasa = '', genre = '') =>
  t.get<LaguTop[]>(`/${kode}/top`, { params: { sumber, bahasa: bahasa || undefined, genre: genre || undefined } }).then(r => r.data)
// 26 Okt: genre yang ada di katalog Top ElGroup untuk bahasa terpilih.
// 30 Okt: genre = KODE kategori hasil pemetaan tim (sama dengan chip
// Spotify/YouTube); nama/nama_i18n = label chip.
export type GenreKami = { genre: string; jumlah: number; nama?: string; nama_i18n?: Partial<Record<BahasaTop, string>> | null }
export const ambilGenreKami = (kode: string, bahasa = '') =>
  t.get<GenreKami[]>(`/${kode}/top`, { params: { sumber: 'kami_genre', bahasa: bahasa || undefined } }).then(r => r.data)
// Sampul lagu (iTunes lewat server, disinggahkan di server + localStorage).
// Antrean satu-per-satu supaya tablet tidak menembak 50 permintaan sekaligus.
const KUNCI_ART = 'kr_art_v1'
let artCache: Record<string, string> = {}
try { artCache = JSON.parse(localStorage.getItem(KUNCI_ART) || '{}') } catch { artCache = {} }
const artTunggu: (() => Promise<void>)[] = []
let artJalan = false
async function artKerjakan() {
  if (artJalan) return
  artJalan = true
  while (artTunggu.length) { await artTunggu.shift()!() }
  artJalan = false
}
export function sampul(kode: string, judul: string, artis: string): Promise<string> {
  const k = (artis + '|' + judul).toLowerCase()
  if (k in artCache) return Promise.resolve(artCache[k])
  return new Promise(res => {
    artTunggu.push(async () => {
      try {
        const r = await t.get<{ url: string }>(`/${kode}/art`, { params: { judul, artis } })
        artCache[k] = r.data.url || ''
        try {
          const simpan = Object.keys(artCache).length > 600 ? {} : artCache
          localStorage.setItem(KUNCI_ART, JSON.stringify(simpan))
        } catch { /* penuh — biarkan */ }
        res(artCache[k])
      } catch { res('') }
    })
    void artKerjakan()
  })
}

// Tablet kamar (29 Sep 2026): token disimpan di tablet, remote terbuka sendiri.
export const KUNCI_PERANGKAT = 'kr_perangkat'
export type JawabPerangkat = { room: string; outlet: string; kode: string; status: string }
export const cekPerangkat = (token: string) =>
  t.get<JawabPerangkat>(`/perangkat/${encodeURIComponent(token)}`).then(r => r.data)

export const ambilPromo = (kode: string) =>
  t.get<PromoTamu[]>(`/${kode}/promo`).then(r => r.data)

// 2 Okt 2026 — layanan room: kontak marketing, pesanan F&B, panggil waitress.
export type BarisPesanan = {
  nama: string; qty: number; uom: string; catatan: string
  status: 'belum' | 'diproses' | 'diantar'
}
// 5 Okt 2026: status panggilan per jenis. menunggu = belum di-OK penerima,
// menuju = sudah di-OK ("… sedang menuju"), '' = tidak ada.
export type StatusPanggil = { status: '' | 'menunggu' | 'menuju'; at: string; oleh: string }
export type Layanan = {
  marketing: { nama: string; hp: string; bisa_dipanggil?: boolean } | null
  pesanan: {
    bill: { id: string; status: string; baris: BarisPesanan[] }[]
    n_belum: number; n_diproses: number; n_diantar: number
  } | null
  panggilan_aktif: string
  panggil?: { waitress: StatusPanggil; marketing: StatusPanggil }
}
/** Jawaban tombol panggil. `kode`: dipanggil | sudah (dalam 60 dtk) | tak_ada. */
export type JawabPanggil = { ok: boolean; kode?: 'dipanggil' | 'sudah' | 'tak_ada'; pesan: string }
export const ambilLayanan = (kode: string) =>
  t.get<Layanan>(`/${kode}/layanan`).then(r => r.data)
export const panggilWaitress = (kode: string, catatan = '') =>
  t.post<JawabPanggil>(`/${kode}/panggil`, { catatan }).then(r => r.data)
export const panggilMarketing = (kode: string) =>
  t.post<JawabPanggil>(`/${kode}/panggil-marketing`, {}).then(r => r.data)

export const ambilBerkas = (kode: string, songId: string) =>
  t.get<BerkasLagu>(`/${kode}/lagu/${songId}`).then(r => r.data)

export const antre = (kode: string, song_id: string, nama: string) =>
  t.post<JawabAntre>(`/${kode}/antre`, { song_id, nama }).then(r => r.data)

export const setelan = (
  kode: string, id: string,
  isi: {
    geser_nada?: number; tempo_persen?: number; vokal_panduan?: boolean
    musik_nyala?: boolean; jeda?: boolean; volume?: number; ulang?: boolean
    /** 21 Okt 2026: geser lirik (detik, nilai mutlak −30..30) — per video, bukan per baris. */
    lirik_geser?: number
    /** 27 Okt 2026: nyalakan/matikan lirik kita untuk video ini (per video, bawaan mati). */
    lirik_tampil?: boolean
  },
) => t.post(`/${kode}/antrean/${id}/setelan`, isi).then(r => r.data)

export const laporPemutar = (kode: string, id: string, isi: LaporPemutar) =>
  t.post(`/${kode}/antrean/${id}/lapor`, isi).then(r => r.data)

/** Pemutar TV → posisi & durasi lagu yang sedang diputar (progress bar remote). */
export const laporPosisi = (kode: string, id: string,
  isi: { posisi: number; durasi: number; jalan: boolean; laju: number }) =>
  t.post(`/${kode}/antrean/${id}/posisi`, isi).then(r => r.data)

// ── KATALOG LUAR: SPOTIFY & YOUTUBE (3 Okt 2026) ───────────────────────────
//
// Klien yang sama dipakai remote tamu (/api/t/:kode/luar/…, dijaga kode sesi)
// dan layar staf (/api/luar/…, dijaga token) — lihat `klienLuar` di bawah.

export type LaguLuar = {
  sumber: 'spotify' | 'youtube'
  id: string            // track id / video id ('' = belum diketahui)
  judul: string; artis: string
  album?: string; sampul?: string
  durasi?: number; posisi?: number
  song_id: string | null // ada di katalog kita & siap dinyanyikan
}
export type Koleksi = {
  jenis: 'album' | 'playlist' | 'artis'
  id: string; nama: string; subjudul?: string; sampul?: string; jumlah?: number
}
export type IsiKoleksi = Koleksi & { lagu: LaguLuar[]; album?: Koleksi[]; asal: string }
export type KategoriLuar = { kode: string; nama: string; khusus: string | null }
export type StatusLuar = { spotify_kunci: boolean; youtube_kunci: boolean; kategori: KategoriLuar[] }
export type IsiKategori = { kode: string; nama: string; koleksi: Koleksi[]; lagu: LaguLuar[]; catatan?: string[] }
export type HasilCariSpotify = { lagu: LaguLuar[]; album: Koleksi[]; artis: Koleksi[]; asal: string }
export type HasilCariYouTube = { video: LaguLuar[]; perlu_kunci: boolean; kueri?: string }

export type KlienLuar = {
  status: () => Promise<StatusLuar>
  kategori: (kat: string) => Promise<IsiKategori>
  koleksi: (jenis: Koleksi['jenis'], id: string) => Promise<IsiKoleksi>
  cariSpotify: (q: string) => Promise<HasilCariSpotify>
  cariYouTube: (q: string, karaoke: boolean) => Promise<HasilCariYouTube>
  chartYouTube: () => Promise<LaguLuar[]>
}

/** `get` = fungsi GET ber-awalan (tamu: '/api/t/KODE/luar', staf: '/api/luar'). */
export function buatKlienLuar(get: <T>(jalur: string, params?: Record<string, string>) => Promise<T>): KlienLuar {
  return {
    status: () => get<StatusLuar>('/status'),
    kategori: kat => get<IsiKategori>(`/spotify/kategori/${encodeURIComponent(kat)}`),
    koleksi: (jenis, id) => get<IsiKoleksi>(`/spotify/${jenis}/${encodeURIComponent(id)}`),
    cariSpotify: q => get<HasilCariSpotify>('/spotify/cari', { q }),
    cariYouTube: (q, karaoke) => get<HasilCariYouTube>('/youtube/cari', { q, karaoke: karaoke ? '1' : '0' }),
    chartYouTube: () => get<LaguLuar[]>('/youtube/chart'),
  }
}

export const klienLuarTamu = (kode: string): KlienLuar =>
  buatKlienLuar(<T,>(jalur: string, params?: Record<string, string>) =>
    t.get<T>(`/${kode}/luar${jalur}`, { params }).then(r => r.data))

// `diputar` (5 Okt 2026): server langsung memutar lagu ini karena tidak ada
// yang sedang diputar — remote menampilkan "diputar sekarang".
export type JawabAntre = { id: string; urutan: number; diputar?: boolean }

export const antreLuar = (kode: string, isi: {
  sumber: 'spotify' | 'youtube'; ref: string; judul: string; artis: string
  sampul?: string; durasi?: number; nama: string
}) => t.post<JawabAntre>(`/${kode}/antre-luar`, isi).then(r => r.data)

// ── 5 Okt 2026: TOP PER BAHASA + KARAOKE SATU KETUKAN ──────────────────────
export type BahasaTop = 'id' | 'en' | 'zh' | 'ja' | 'ko'
export type HasilTop = { lagu: LaguLuar[]; asal?: string; catatan?: string[]; diperbarui?: string }
/** genre '' = Top (daftar per bahasa); selain itu kode kr_luar_genre. */
export const ambilTopBahasa = (kode: string, sumber: 'spotify' | 'youtube', bahasa: BahasaTop, genre = '') =>
  t.get<HasilTop>(`/${kode}/luar/top`, { params: genre ? { sumber, bahasa, genre } : { sumber, bahasa } }).then(r => r.data)

// 5 Okt 2026: sub-kategori genre per bahasa (tabel kr_luar_genre). `sp`/`yt`
// = status singgahan server: `ada` false → chip tampil redup, isinya
// "belum tersedia" sampai pemanas server selesai menarik.
export type StatusGenre = {
  ada: boolean; jumlah: number; asal?: string; diperbarui?: string; sampai?: string
  override?: string; pilih?: string; pilih_nama?: string; pilih_at?: string; dicari_at?: string
}
export type GenreLuar = {
  bahasa: BahasaTop; kode: string; nama: string
  nama_i18n: Partial<Record<BahasaTop, string>> | null
  sp: StatusGenre; yt: StatusGenre
}
export const ambilGenre = (kode: string) =>
  t.get<{ genre: GenreLuar[] }>(`/${kode}/luar/genre`).then(r => r.data.genre ?? [])

/** Lagu Spotify di luar katalog → server mencari versi karaoke di YouTube
 *  (disinggahkan per trek) lalu langsung mengantrekannya. */
export const antreKaraoke = (kode: string, isi: { judul: string; artis: string; sampul?: string; nama: string }) =>
  t.post<JawabAntre & { video?: { id: string; judul: string } }>(`/${kode}/antre-karaoke`, isi).then(r => r.data)

/** Kode galat mesin dari server (mis. 'kuota', 'kunci', 'tidak_ada'). */
export const kodeGalat = (e: unknown): string =>
  (e as { response?: { data?: { kode?: string } } })?.response?.data?.kode ?? ''

/** ref antrean untuk trek Spotify: id 22 karakter, atau "cari:<kueri>". */
export const refSpotify = (l: { id: string; judul: string; artis: string }) =>
  l.id ? l.id : `cari:${`${l.judul} ${l.artis}`.trim().slice(0, 150)}`

/** URI aplikasi Spotify + tautan web cadangan. */
export function uriSpotify(ref: string): { app: string; web: string } {
  if (ref.startsWith('cari:')) {
    const q = ref.slice(5)
    return { app: `spotify:search:${encodeURIComponent(q)}`, web: `https://open.spotify.com/search/${encodeURIComponent(q)}` }
  }
  return { app: `spotify:track:${ref}`, web: `https://open.spotify.com/track/${ref}` }
}

export const batalAntre = (kode: string, id: string) =>
  t.delete(`/${kode}/antrean/${id}`).then(r => r.data)
// 27 Okt 2026: urutan baru lagu yang masih menunggu (drag & drop di remote).
export const urutkanAntrean = (kode: string, ids: string[]) =>
  t.post(`/${kode}/antrean/urutkan`, { ids }).then(r => r.data)

export const tandai = (kode: string, id: string, aksi: 'mulai' | 'selesai' | 'lewati') =>
  t.post(`/${kode}/antrean/${id}/${aksi}`).then(r => r.data)

export const kirimSkor = (kode: string, isi: {
  antrean_id: string; song_id: string; nama: string
  skor: number; mode: string; rincian: unknown
  detik_bersuara: number; geser_nada: number; tempo_persen: number
}) => t.post(`/${kode}/skor`, isi).then(r => r.data)

/** Nama tamu diingat per perangkat — HP yang sama biasanya orang yang sama. */
const KUNCI_NAMA = 'karaoke.tamu.nama'
export const bacaNama = () => localStorage.getItem(KUNCI_NAMA) || ''
export const simpanNama = (n: string) => localStorage.setItem(KUNCI_NAMA, n.trim())

export const jam = (detik: number | null | undefined): string => {
  if (detik == null) return '—'
  const j = Math.floor(detik / 3600)
  const m = Math.floor((detik % 3600) / 60)
  const d = Math.floor(detik % 60)
  const dua = (n: number) => String(n).padStart(2, '0')
  return j > 0 ? `${j}:${dua(m)}:${dua(d)}` : `${m}:${dua(d)}`
}

/** "+2" → "D → E". Butuh nada dasar; tanpa itu kembalikan angkanya saja. */
const NAMA_NADA = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
export function nadaTampil(dasar: string | null, geser: number): string {
  if (geser === 0) return 'Nada asli'
  const tanda = geser > 0 ? `+${geser}` : `${geser}`
  if (!dasar) return tanda
  // Hanya kres yang dikenali. Nada mol ('B♭', 'Eb') sengaja TIDAK
  // diterjemahkan: memetakannya asal — 'B♭' menjadi 'BB' — menghasilkan nama
  // nada yang salah di layar, dan nama nada yang salah lebih buruk daripada
  // menampilkan '+2' saja.
  const bersih = dasar.trim().toUpperCase().replace('♯', '#')
  const i = NAMA_NADA.indexOf(bersih)
  if (i < 0) return tanda
  const j = ((i + geser) % 12 + 12) % 12
  return `${bersih} → ${NAMA_NADA[j]} (${tanda})`
}
